// ── Marketing Department Resolution System ──────────────────────────────────
// Pure functions for campaign advancement, tour evaluation, PR crises,
// and weekly marketing tick. Called from reducer day/week hooks.

import { CAMPAIGN_TEMPLATES, pickWeeklySlots } from '../data/marketingCampaigns';
import { hasMarketingDept } from './marketingHelpers';

// ── Constants ────────────────────────────────────────────────────────────────
const DEMAND_MIN = 0.85;
const DEMAND_MAX = 1.30;
const DEMAND_DECAY_RATE = 0.02;         // weekly decay toward 1.0
const SATURATION_DECAY_PER_WEEK = 1;    // socialSaturation drops by 1/wk
const WEEKLY_BRAND_CAP = 10;            // max brand gain from marketing per week
const MAX_TOURS_PER_MONTH = 2;
const TOUR_FEE = 500;
const TOUR_CANCEL_REFUND = 0.5;

function clampDemand(d) {
  return Math.max(DEMAND_MIN, Math.min(DEMAND_MAX, d));
}

// ── Campaign Day Advancement ─────────────────────────────────────────────────
// Called when gameDay increments. Decrements daysRemaining on active campaigns;
// returns { marketing, completedCampaigns } with completed ones removed from active.
export function advanceCampaignDays(marketing) {
  const completed = [];
  const still = [];
  for (const c of marketing.activeCampaigns) {
    const updated = { ...c, daysRemaining: c.daysRemaining - 1 };
    if (updated.daysRemaining <= 0) {
      completed.push(updated);
    } else {
      still.push(updated);
    }
  }
  return {
    marketing: { ...marketing, activeCampaigns: still },
    completedCampaigns: completed,
  };
}

// ── Campaign Resolution ──────────────────────────────────────────────────────
// Resolves a completed campaign: quality check, backlash roll, brand/demand deltas.
// recentAvgQuality: average qualityScore of recent sales (0-100).
// rng: deterministic random 0-1 for testing.
export function resolveCompletedCampaign(campaign, recentAvgQuality, socialSaturation, rng) {
  const template = CAMPAIGN_TEMPLATES.find(t => t.id === campaign.templateId) || campaign;
  const qualityMet = recentAvgQuality >= (template.qualityFloor || 0);

  // Saturation penalty: effectiveness drops when oversaturated
  const satPenalty = socialSaturation > 5 ? 0.5 : socialSaturation > 3 ? 0.75 : 1.0;

  // Backlash check: risk increases if quality floor not met
  const effectiveRisk = qualityMet ? template.backlashRisk * 0.2 : template.backlashRisk;
  const backlash = rng() < effectiveRisk;

  let brandDelta = 0;
  let demandDelta = 0;
  let message = '';

  if (backlash) {
    // Backlash: lose brand, no demand gain
    brandDelta = -Math.ceil(template.brandDelta * 0.5);
    demandDelta = -0.02;
    message = `📉 Campaign "${template.name}" backfired! Quality didn't match the hype.`;
  } else {
    // Success: apply deltas scaled by saturation
    brandDelta = Math.round(template.brandDelta * satPenalty);
    demandDelta = template.demandDelta * satPenalty;
    message = brandDelta > 0
      ? `📈 Campaign "${template.name}" completed! Brand +${brandDelta}, demand boost applied.`
      : `📊 Campaign "${template.name}" completed, but market saturation limited the impact.`;
  }

  return {
    brandDelta,
    demandDelta,
    backlash,
    qualityMet,
    saturationIncrease: template.saturationCost || 0,
    message,
  };
}

// ── Weekly Marketing Tick ────────────────────────────────────────────────────
// Called during PROCESS_WEEK. Handles budget deduction, decay, slot refresh,
// passive brand rules, and weekly cap enforcement.
export function weeklyMarketingTick(state, rng) {
  const mkt = state.marketing || {};
  const budget = mkt.weeklyBudget || 0;

  // Budget deduction (returned as negative cash delta)
  const cashDelta = -budget;

  // Demand decay toward 1.0
  let newDemand = state.demandModifier ?? 1.0;
  if (newDemand > 1.0) {
    newDemand = Math.max(1.0, newDemand - DEMAND_DECAY_RATE);
  } else if (newDemand < 1.0) {
    newDemand = Math.min(1.0, newDemand + DEMAND_DECAY_RATE);
  }

  // Social saturation decay (faster at higher budget)
  const budgetDecayBonus = budget >= 500 ? 1 : 0;
  const newSaturation = Math.max(0, (mkt.socialSaturation || 0) - SATURATION_DECAY_PER_WEEK - budgetDecayBonus);

  // Refresh campaign slots
  const newSlots = pickWeeklySlots(state, rng);

  // Brand inactivity decay: if no budget and no active campaigns, brand drifts down
  let brandDelta = 0;
  const hasActivity = budget > 0 || (mkt.activeCampaigns || []).length > 0;
  if (!hasActivity && hasMarketingDept(state)) {
    brandDelta = -1; // slow inactivity decay
  }

  return {
    cashDelta,
    demandModifier: clampDemand(newDemand),
    brandDelta,  // added to brandValue in reducer (subject to weekly cap)
    marketing: {
      ...mkt,
      socialSaturation: newSaturation,
      availableCampaignSlots: newSlots,
    },
  };
}

// ── Tour Snapshot Evaluation ─────────────────────────────────────────────────
// Evaluates the facility state when a tour resolves.
// Returns brand/rep/demand deltas and a narrative code for notifications.
export function evaluateTourSnapshot(state) {
  const rooms = state.rooms || [];
  const productionRooms = rooms.filter(r => r.department === 'production' && r.unlocked);

  let score = 50; // base score

  // Cleanliness
  const dirtyCount = productionRooms.filter(r => r.needsCleaning).length;
  score -= dirtyCount * 10;

  // Pest/mold risk (average across rooms with batches)
  const batchRooms = productionRooms.filter(r => r.batch);
  if (batchRooms.length > 0) {
    const avgPest = batchRooms.reduce((s, r) => s + (r.batch.pestRisk || 0), 0) / batchRooms.length;
    const avgMold = batchRooms.reduce((s, r) => s + (r.batch.moldRisk || 0), 0) / batchRooms.length;
    score -= Math.round(avgPest * 30);
    score -= Math.round(avgMold * 30);
  }

  // Equipment health
  const avgHealth = productionRooms.reduce((s, r) => {
    const equip = r.equipment || {};
    const healthValues = Object.values(equip)
      .filter(e => typeof e === 'object' && e.health !== undefined)
      .map(e => e.health);
    return s + (healthValues.length > 0 ? healthValues.reduce((a, b) => a + b, 0) / healthValues.length : 100);
  }, 0) / Math.max(1, productionRooms.length);
  score += Math.round((avgHealth - 70) * 0.5); // bonus/penalty around 70% baseline

  // Number of rooms unlocked (scale / impressiveness)
  score += Math.min(15, productionRooms.length * 5);

  // Clamp score 0-100
  score = Math.max(0, Math.min(100, score));

  // Map score to deltas
  let brandDelta, demandDelta, repDelta, narrative;
  if (score >= 75) {
    brandDelta = 4;
    demandDelta = 0.04;
    repDelta = 3;
    narrative = 'tour_great';
  } else if (score >= 50) {
    brandDelta = 1;
    demandDelta = 0.01;
    repDelta = 1;
    narrative = 'tour_ok';
  } else {
    brandDelta = -3;
    demandDelta = -0.02;
    repDelta = -2;
    narrative = 'tour_bad';
  }

  return { score, brandDelta, demandDelta, repDelta, narrative };
}

// ── Tour Narrative Messages ──────────────────────────────────────────────────
export function getTourMessage(narrative) {
  switch (narrative) {
    case 'tour_great':
      return '🎉 Facility tour was a hit! Visitors were impressed by the clean, well-maintained operation.';
    case 'tour_ok':
      return '🏭 Tour went fine. Visitors saw a functional grow, nothing special.';
    case 'tour_bad':
      return '😬 Tour guests noticed dirty rooms and pest issues. Bad press incoming.';
    default:
      return '🏭 Tour completed.';
  }
}

// ── PR Crisis Resolution ─────────────────────────────────────────────────────
// Player chooses a response; returns brand/rep/cash deltas.
export function resolvePRCrisis(crisis, response) {
  // response: 'apologize' | 'deny' | 'ignore'
  const base = crisis.severity || 1; // 1=minor, 2=moderate, 3=major

  switch (response) {
    case 'apologize':
      return {
        brandDelta: -base,       // small brand hit (accepting blame)
        repDelta: base,          // rep recovery (shows accountability)
        cashDelta: -200 * base,  // cost of PR effort
        message: `🤝 You apologized for "${crisis.description}". Brand dipped but reputation recovered.`,
      };
    case 'deny':
      return {
        brandDelta: Math.random() < 0.4 ? -base * 2 : 1,  // risky — could backfire or work
        repDelta: -base,
        cashDelta: 0,
        message: `🙅 You denied "${crisis.description}". Risky move — time will tell.`,
      };
    case 'ignore':
    default:
      return {
        brandDelta: -base * 2,   // worst brand hit
        repDelta: -base,         // rep suffers too
        cashDelta: 0,
        message: `🙈 You ignored "${crisis.description}". The press noticed.`,
      };
  }
}

// ── Exports for constants (for tests / UI) ───────────────────────────────────
export {
  DEMAND_MIN, DEMAND_MAX, DEMAND_DECAY_RATE,
  WEEKLY_BRAND_CAP, MAX_TOURS_PER_MONTH, TOUR_FEE, TOUR_CANCEL_REFUND,
};
